import { UpdatePage } from '@/components/molecules';
import {
  TextFieldElement,
  useForm,
  UseFormSetValue,
  AutocompleteElement,
} from 'react-hook-form-mui';
import { Box, Grid, Typography } from '@mui/material';
import API from '@/apps/settings/services/settings';
import { ModelProps } from '@/types';
import { useTranslation } from 'react-i18next';
import { JSX, Fragment } from 'react';
import { UpdateSchema, UpdateSchemaType } from '../schemas/settings';
import { zodResolver } from '@hookform/resolvers/zod';
import { setDefaultLanguageValues } from '@apps/localization/services/utils';
import { LANGUAGES_AVAILABLE } from '@/const';

export default function MachineUpdatePage(modelProps: ModelProps): JSX.Element {
  const { t } = useTranslation();

  const defaultValues = {
    translations: LANGUAGES_AVAILABLE.map((lang, idx) => ({
      language_code: {
        id: lang.id,
        label: lang.label,
      },
      inlineIndex: idx,
    })),
  };

  const formContext = useForm<UpdateSchemaType>({
    resolver: zodResolver(UpdateSchema),
    defaultValues: defaultValues,
  });

  return (
    <UpdatePage
      schema={UpdateSchema}
      api={API}
      modelProps={modelProps}
      hideBack={true}
      title={t('Settings')}
      formcontext={formContext}
      setChildValues={(setValue) => {
        setDefaultLanguageValues(setValue as UseFormSetValue<UpdateSchemaType>);
      }}
      customPath={'settings/settings/add-update/1'}
    >
      <>
        <Box
          sx={{ flexWrap: 'wrap', display: 'flex', width: '100%', gap: '1rem' }}
        >
          <Box
            sx={{
              border: '1px solid #1e9d8b',
              borderRadius: '8px',
              flex: 1,
              overflow: 'hidden',
              minWidth: '100%',
            }}
          >
            <Typography
              variant="h6"
              textAlign={'center'}
              sx={{
                background: '#1e9d8b',
                color: '#fff',
                padding: '0.3rem 1rem',
              }}
            >
              {t('General')}
            </Typography>
            <Grid container sx={{ padding: 2 }} spacing={2}>
              {LANGUAGES_AVAILABLE.map((_item, idx) => (
                <Fragment key={idx}>
                  <TextFieldElement
                    name={`translations.${idx}.inlineIndex`}
                    value={idx}
                    type="hidden"
                    sx={{ display: 'none' }}
                  />
                  <Grid size={{ xs: 12, md: 6 }}>
                    <AutocompleteElement
                      name={`translations.${idx}.language_code`}
                      label={t('Language')}
                      options={LANGUAGES_AVAILABLE}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextFieldElement
                      name={`translations.${idx}.nnc_url`}
                      label={'NNC - URL'}
                      required
                      fullWidth
                    />
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Box>
          <Box
            sx={{
              border: '1px solid #1e9d8b',
              borderRadius: '8px',
              flex: 1,
              overflow: 'hidden',
              minWidth: '100%',
            }}
          >
            <Typography
              variant="h6"
              textAlign={'center'}
              sx={{
                background: '#1e9d8b',
                color: '#fff',
                padding: '0.3rem 1rem',
              }}
            >
              {t('Support File')}
            </Typography>
            <Grid container sx={{ padding: 2 }} spacing={2}>
              {LANGUAGES_AVAILABLE.map((_item, idx) => (
                <Fragment key={idx}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <AutocompleteElement
                      name={`translations.${idx}.language_code`}
                      label={t('Language')}
                      options={LANGUAGES_AVAILABLE}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextFieldElement
                      name={`translations.${idx}.nnc_support`}
                      label={'URL'}
                      required
                      fullWidth
                    />
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Box>
        </Box>
      </>
    </UpdatePage>
  );
}
